import { useState } from 'react';

// THE TERM, AS DAVID CONFIRMS IT (client-bind-flow decision 14, S5).
//
// 12 months is PRE-PICKED on a new log (the caller seeds '12'), with 6 months
// and Other beside it. What is sent is what the picker shows: the caller sends
// termMonthsOf(value), and never a fallback of its own. The server stores a
// blank term as blank, and the present gate lists 'term' rather than inventing
// one (routes/tracon.routes.js statedTermMonths).
//
// An EXISTING quote seeds the picker from its stored term, and a stored blank
// seeds nothing: a placeholder must not be spelled like an answer, so an edit
// never writes a 12 nobody picked.
//
// No imports beyond React, so the node tests execute it as it ships
// (tests/term-picker.test.js).

export const TERM_CHOICES = ['12', '6'];
export const TERM_MAX_MONTHS = 36;

// A whole number of months from 1 to TERM_MAX_MONTHS, or null (not stated, or
// not a term). Text never becomes a number here.
export function termMonthsOf(value) {
  const s = value == null ? '' : String(value).trim();
  if (!/^\d{1,2}$/.test(s)) return null;
  const n = Number(s);
  return n >= 1 && n <= TERM_MAX_MONTHS ? n : null;
}

// The seed for an existing quote's stored term: its own value, or nothing.
export function termSeed(stored) {
  const n = termMonthsOf(stored);
  return n == null ? '' : String(n);
}

const seg = (on) => [
  'rounded-[5px] border px-2 py-0.5 text-[12px] disabled:opacity-50',
  on ? 'border-accent bg-accent text-white' : 'border-border text-text-muted hover:border-border-strong hover:text-text',
].join(' ');

export default function TermPicker({ value, onChange, disabled = false, label = 'Term' }) {
  const v = value == null ? '' : String(value);
  // Other is open when the term is neither 12 nor 6, or he clicked it and the
  // value is not 12 or 6 since: a caller that resets the value to its pre-pick
  // shows the pre-pick pressed, never an open Other box holding a 12.
  const [otherOpen, setOtherOpen] = useState(v !== '' && !TERM_CHOICES.includes(v));
  const other = !TERM_CHOICES.includes(v) && (otherOpen || v !== '');
  const typedBad = other && v !== '' && termMonthsOf(v) == null;
  return (
    <span className="inline-flex flex-wrap items-center gap-1.5" data-term-picker>
      {label ? <span className="text-[11px] text-text-dim">{label}</span> : null}
      {TERM_CHOICES.map((t) => (
        <button key={t} type="button" disabled={disabled} aria-pressed={!other && v === t}
          onClick={() => { setOtherOpen(false); onChange(t); }} className={seg(!other && v === t)}>
          {t} months
        </button>
      ))}
      <button type="button" disabled={disabled} aria-pressed={other}
        onClick={() => { setOtherOpen(true); if (TERM_CHOICES.includes(v)) onChange(''); }} className={seg(other)}>
        Other
      </button>
      {other ? (
        <span className="inline-flex items-center gap-1">
          <input type="text" inputMode="numeric" maxLength={2} value={v} disabled={disabled} aria-label="Term in months"
            onChange={(e) => onChange(e.target.value.replace(/\D/g, '').slice(0, 2))}
            placeholder="months"
            className="w-16 rounded border border-border bg-bg px-2 py-0.5 text-[12px] text-text placeholder:text-text-dim focus:border-accent focus:outline-none" />
          <span className="text-[11px] text-text-dim">months</span>
        </span>
      ) : null}
      {typedBad ? <span className="text-[11px] text-danger">1 to {TERM_MAX_MONTHS} months</span> : null}
      {v === '' && !other ? <span className="text-[11px] text-warning">not stated</span> : null}
    </span>
  );
}
